/**
 * B2BLoginPage — Dual-mode authentication for B2B portal
 *
 * Phase 32 (Revised): Two explicit login tabs give the user full control:
 *
 *   Tab A — "קוד אימות" (OTP, DEFAULT)
 *     • Single input: email OR Israeli mobile number
 *     • Supabase signInWithOtp({ email }) or signInWithOtp({ phone })
 *     • 6-digit OTP box grid → verifyOtp() → classifyAuthenticatedUser()
 *
 *   Tab B — "מייל וסיסמה" (Password)
 *     • Two inputs: email + password
 *     • Supabase signInWithPassword({ email, password })
 *     • Immediately → classifyAuthenticatedUser() on success
 *
 *  Post-login (BOTH tabs):
 *    • classifyAuthenticatedUser() checks inventory_clients + inventory_agents
 *    • Unknown users → signOut() + "Access Denied" error shown in-card
 *
 * Route:  /b2b  (public — auth guard lives in B2BRoute)
 * Design: mobile-first, RTL Hebrew, single centred card ≤ 420 px wide.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase }       from '@/lib/supabase';
import { classifyAuthenticatedUser, type B2bClientSession, type AgentSession } from '@/lib/b2bMarketplaceService';
import {
  useB2BUser,
  type B2BUserSession,
  type AgentCompanyOption,
} from './B2BUserContext';
import { Loader as Loader2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, ArrowRight, ShoppingBag, Mail, Phone, RefreshCw, Lock, KeyRound, Eye, EyeOff, Briefcase, Store, Send } from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns true when the string looks like an email address. */
const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

/**
 * Returns true when the string looks like an Israeli mobile / international
 * phone number.  Intentionally permissive; Supabase rejects invalid numbers.
 */
const isPhone = (v: string) => /^[+]?[\d\s\-().]{7,16}$/.test(v.trim());

/**
 * Normalise a local Israeli mobile to E.164 for Supabase OTP.
 *   050-1234567  →  +972501234567
 */
function normalisePhone(raw: string): string {
  const digits = raw.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) return digits;
  if (digits.startsWith('0'))  return `+972${digits.slice(1)}`;
  return `+${digits}`;
}

// ─── Role Selector ────────────────────────────────────────────────────────────

/**
 * Shown when a user's email/phone exists in BOTH inventory_clients and
 * inventory_agents.  The user explicitly picks which role to continue as.
 */
interface RoleSelectorProps {
  b2bSession:   B2bClientSession;
  agentSession: AgentSession;
  onSelectClient: () => void;
  onSelectAgent:  () => void;
}

function RoleSelector({ b2bSession, agentSession, onSelectClient, onSelectAgent }: RoleSelectorProps) {
  const clientName = b2bSession.clientRecords[0]?.name ?? '';
  const agentName  = agentSession.agentName ?? agentSession.agentEmail ?? '';

  return (
    <div dir="rtl" className="space-y-4">
      <div className="text-center">
        <h2 className="text-lg font-bold text-gray-900">בחר תפקיד להתחברות</h2>
        <p className="text-sm text-gray-500 mt-1">
          הפרטים שלך רשומים גם כלקוח וגם כסוכן — באיזה תפקיד תרצה להמשיך?
        </p>
      </div>

      {/* Client option */}
      <button
        onClick={onSelectClient}
        className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-200
                   hover:border-primary-400 hover:bg-primary-50 transition-colors text-right group"
      >
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary-100
                        group-hover:bg-primary-200 transition-colors shrink-0">
          <Store className="w-6 h-6 text-primary-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm">המשך כלקוח</p>
          {clientName && (
            <p className="text-xs text-gray-500 truncate mt-0.5">{clientName}</p>
          )}
          <p className="text-xs text-gray-400 mt-0.5">הזמנות, קטלוג מוצרים</p>
        </div>
        <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-primary-500 transition-colors shrink-0" />
      </button>

      {/* Agent option */}
      <button
        onClick={onSelectAgent}
        className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-200
                   hover:border-orange-400 hover:bg-orange-50 transition-colors text-right group"
      >
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-orange-100
                        group-hover:bg-orange-200 transition-colors shrink-0">
          <Briefcase className="w-6 h-6 text-orange-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm">המשך כסוכן</p>
          {agentName && (
            <p className="text-xs text-gray-500 truncate mt-0.5">{agentName}</p>
          )}
          <p className="text-xs text-gray-400 mt-0.5">ניהול לקוחות, הגשת הזמנות מטעמם</p>
        </div>
        <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-orange-500 transition-colors shrink-0" />
      </button>
    </div>
  );
}

// ─── Types for edge function response records ───────────────────────────────

type EdgeClientRecord = {
  id: string; company_id: string; name: string; email: string | null;
  phone: string | null; pricelist_id: string | null; business_id: string | null;
  agent_id?: string | null; is_vat_liable?: boolean; client_access?: string;
};

type EdgeAgentRecord = {
  id: string; company_id: string; name: string; email: string | null;
  phone: string | null; sales_commission_rate: number | null;
  collection_commission_rate: number | null;
  is_master_agent?: boolean;
  default_warehouse_id?: string | null;
};

function buildB2bSessionFromRecords(clientRecords: EdgeClientRecord[]): B2bClientSession {
  return {
    clientRecords: clientRecords.map(r => ({
      id: r.id, company_id: r.company_id, name: r.name,
      email: r.email, phone: r.phone, pricelist_id: r.pricelist_id,
      business_id: r.business_id ?? null, is_vat_liable: r.is_vat_liable ?? true,
      client_access: r.client_access ?? 'full',
    })),
    allowedCompanyIds: [...new Set(clientRecords.map(r => r.company_id))],
    pricelistByCompany: Object.fromEntries(
      clientRecords.map(r => [r.company_id, r.pricelist_id]),
    ),
  };
}

function buildAgentSessionFromRecord(rec: EdgeAgentRecord): AgentSession {
  return {
    userId:                   rec.id,
    agentProfileId:           rec.id,
    companyId:                rec.company_id,
    role:                     'field_agent',
    modulePermissions:        {},
    agentName:                rec.name,
    agentEmail:               rec.email,
    agentPhone:               rec.phone,
    salesCommissionRate:      rec.sales_commission_rate,
    collectionCommissionRate: rec.collection_commission_rate,
    defaultWarehouseId:       rec.default_warehouse_id ?? null,
    isMasterAgent:            rec.is_master_agent ?? false,
    masterAgentCompanyIds:    rec.is_master_agent ? [rec.company_id] : [],
    agentClientRecords:       [],
  };
}

/**
 * Route and seed the B2B session from edge function response data.
 *
 * Used by the WhatsApp/SMS OTP verify handlers after the auth session has been
 * established via verifyOtp. Instead of re-fetching client/agent records from
 * the DB (which can race with the just-created auth session), we use the
 * records the edge function already returned with service-role access.
 *
 * Returns:
 *   - null   → navigation done
 *   - string → error message
 *   - { dual_role } → caller should show the Role Selector
 */
async function routeFromEdgeData(
  clientRecords: EdgeClientRecord[],
  agentRecords:  EdgeAgentRecord[],
  seedB2BUser:   (s: B2BUserSession) => void,
): Promise<null | string | { dual_role: true; b2bSession: B2bClientSession; agentSession: AgentSession }> {
  const hasClient = clientRecords.length > 0;
  const hasAgent  = agentRecords.length > 0;

  if (!hasClient && !hasAgent) {
    return 'הפרטים שלך לא נמצאו במערכת — צור קשר עם הספק שלך לקבלת גישה';
  }

  if (hasClient && hasAgent) {
    return {
      dual_role:    true,
      b2bSession:   buildB2bSessionFromRecords(clientRecords),
      agentSession: buildAgentSessionFromRecord(agentRecords[0]!),
    };
  }

  if (hasClient) {
    const session = await buildClientSession(buildB2bSessionFromRecords(clientRecords));
    if (!session) return 'פרטי הלקוח לא נמצאו — צור קשר עם הספק שלך';
    seedB2BUser(session);
    window.location.replace('/b2b/catalog');
    return null;
  }

  // hasAgent only
  const agentSess = await buildAgentSession(buildAgentSessionFromRecord(agentRecords[0]!));
  seedB2BUser(agentSess);
  window.location.replace('/b2b/agent');
  return null;
}

/** Apply the result from routeFromEdgeData or classifyAndRoute to local state. */
function applyRouteResult(
  result: Awaited<ReturnType<typeof routeFromEdgeData>>,
  setAccessError: (e: string | null) => void,
  setOtpStep:     (s: OtpStep) => void,
  setDualRole:    (d: { b2bSession: B2bClientSession; agentSession: AgentSession } | null) => void,
) {
  if (result === null) return;
  if (typeof result === 'string') {
    setAccessError(result);
    setOtpStep('contact');
  } else if ('dual_role' in result) {
    setDualRole({ b2bSession: result.b2bSession, agentSession: result.agentSession });
  }
}

// ─── Session builders (shared between classifyAndRoute and RoleSelector) ──────

async function buildClientSession(
  b2b: B2bClientSession,
  preloadedCompanies?: { id: string; legal_name: string }[],
): Promise<B2BUserSession | null> {
  const firstClient = b2b.clientRecords[0];
  if (!firstClient) return null;

  // Distinct company_ids across all client records (may span multiple suppliers)
  const companyIds = [...new Set(b2b.clientRecords.map(r => r.company_id))];

  // Fetch company names for the supplier switcher.
  // Use preloaded data from the RPC when available (avoids RLS issues for
  // phone-only B2B users who may not have company_members rows).
  let availableCompanies: AgentCompanyOption[] = [];
  if (companyIds.length > 0) {
    let nameById: Record<string, string> = {};
    if (preloadedCompanies && preloadedCompanies.length > 0) {
      for (const c of preloadedCompanies) {
        nameById[c.id] = c.legal_name ?? '';
      }
    } else {
      const { data: companies } = await supabase
        .from('companies')
        .select('id, legal_name')
        .in('id', companyIds);
      for (const c of companies ?? []) {
        nameById[(c as { id: string }).id] =
          (c as { legal_name?: string }).legal_name ?? '';
      }
    }
    availableCompanies = companyIds.map(id => ({
      company_id:   id,
      company_name: nameById[id] ?? '',
    }));
  }

  // Scope to the first supplier — the user can switch via the supplier switcher
  const activeCompanyId = firstClient.company_id;
  const firstCompanyRecords = b2b.clientRecords.filter(
    r => r.company_id === activeCompanyId,
  );
  const activeRecord = firstCompanyRecords[0] ?? firstClient;

  return {
    role:               'client',
    profileId:          activeRecord.id,
    agentRecord:        null,
    activeClientId:     activeRecord.id,
    activeClientRecord: {
      id:           activeRecord.id,
      company_id:   activeRecord.company_id,
      name:         activeRecord.name,
      email:        activeRecord.email,
      phone:        activeRecord.phone,
      pricelist_id: activeRecord.pricelist_id,
      business_id:  activeRecord.business_id ?? null,
    },
    clientRecords:      b2b.clientRecords,
    agentClientRecords: [],
    allowedCompanyIds:  [activeCompanyId],
    pricelistByCompany: b2b.pricelistByCompany,
    availableCompanies,
    activeCompanyId,
  };
}

/**
 * Phase 74 — fetch the distinct (company_id, company_name) pairs the agent
 * is registered under via the get_agent_companies_for_jwt() SECURITY DEFINER
 * RPC.  Falls back to a list derived from agentClientRecords +
 * agentRecord.company_id when the RPC is unavailable (e.g. the Phase 74 SQL
 * hasn't been applied yet) so the rest of the portal still boots correctly.
 */
async function fetchAgentCompanies(
  ag: AgentSession,
): Promise<AgentCompanyOption[]> {
  // Try the canonical RPC first.
  const { data, error } = await supabase.rpc('get_agent_companies_for_jwt');
  if (!error && Array.isArray(data) && data.length > 0) {
    return (data as { company_id: string; company_name: string | null }[])
      .map(r => ({
        company_id:   r.company_id,
        company_name: r.company_name ?? '',
      }));
  }
  if (error) {
    console.warn(
      '[Phase 74] get_agent_companies_for_jwt RPC failed:',
      error.message,
      '— falling back to client-derived list. ' +
      'Apply 20260509_phase74_agent_companies.sql to enable canonical lookup.',
    );
  }
  // Fallback: derive the list from agentClientRecords + agentRecord.company_id.
  const ids = [
    ...new Set(
      ag.agentClientRecords.map(r => r.company_id).concat(ag.companyId),
    ),
  ].filter(Boolean);
  if (ids.length === 0) return [];
  const { data: companies } = await supabase
    .from('companies')
    .select('id, legal_name')
    .in('id', ids);
  const nameById: Record<string, string> = {};
  for (const c of companies ?? []) {
    nameById[(c as { id: string }).id] =
      (c as { legal_name?: string }).legal_name ?? '';
  }
  return ids.map(id => ({ company_id: id, company_name: nameById[id] ?? '' }));
}

async function buildAgentSession(ag: AgentSession): Promise<B2BUserSession> {
  const agentClients = ag.agentClientRecords.map(r => ({
    id:           r.id,
    company_id:   r.company_id,
    name:         r.name,
    email:        r.email,
    phone:        r.phone,
    pricelist_id: r.pricelist_id,
    business_id:  r.business_id ?? null,
    consignment_warehouse_id: r.consignment_warehouse_id ?? null,
  }));
  // Phase 40: expose ALL supplier company_ids so the catalog shows products
  // from every supplier the agent is registered with.
  const allAgentCompanyIds = [
    ...new Set(agentClients.map(r => r.company_id).concat(ag.companyId)),
  ];

  // Phase 74: fetch the canonical (company_id, company_name) list for the
  // supplier switcher.  Degrades gracefully when the RPC is unavailable.
  const availableCompanies = await fetchAgentCompanies(ag);

  // Phase 74: pick the initial active company.  Default to the primary
  // agentRecord.company_id when it's in the list, otherwise the first
  // available company.  Single-company agents don't see the switcher.
  const preferred = availableCompanies.find(c => c.company_id === ag.companyId);
  const activeCompanyId =
    preferred?.company_id
    ?? availableCompanies[0]?.company_id
    ?? ag.companyId
    ?? null;

  // Phase 74: when there's a single company the catalog still spans all
  // suppliers (Phase 40 behaviour).  When there are multiple, narrow the
  // catalog to the active company so cross-company leakage is impossible
  // even before a client is selected.
  const initialAllowed =
    availableCompanies.length > 1 && activeCompanyId
      ? [activeCompanyId]
      : allAgentCompanyIds;

  return {
    role:               'agent',
    profileId:          ag.agentProfileId ?? ag.userId,
    agentRecord:        ag.agentProfileId ? {
      id:                           ag.agentProfileId,
      company_id:                   ag.companyId,
      name:                         ag.agentName ?? '',
      email:                        ag.agentEmail,
      phone:                        ag.agentPhone,
      sales_commission_rate:        ag.salesCommissionRate,
      collection_commission_rate:   ag.collectionCommissionRate,
      is_master_agent:              !!(activeCompanyId && (ag.masterAgentCompanyIds ?? []).includes(activeCompanyId)),
      default_warehouse_id:         ag.defaultWarehouseId ?? null,
    } : null,
    masterAgentCompanyIds: ag.masterAgentCompanyIds ?? [],
    activeClientId:     null,
    activeClientRecord: null,
    clientRecords:      [],
    agentClientRecords: agentClients,
    allowedCompanyIds:  initialAllowed,
    pricelistByCompany: {},
    availableCompanies,
    activeCompanyId,
  };
}

// ─── Shared post-login classification ────────────────────────────────────────

/**
 * After any successful Supabase auth (OTP or password), run the B2B
 * authorization check and seed the context / navigate.
 *
 * Returns:
 *   - null          → routing complete, no further action needed
 *   - string        → error message (access denied)
 *   - { dual_role } → caller must show the Role Selector screen
 */
type ClassifyResult =
  | null
  | string
  | { dual_role: true; b2bSession: B2bClientSession; agentSession: AgentSession };

async function classifyAndRoute(
  user:        { id: string; email?: string | null; phone?: string | null },
  seedB2BUser: (s: B2BUserSession) => void,
  originalPhone?: string | null,
): Promise<ClassifyResult> {
  // Pass the original typed phone alongside the auth user's phone/email.
  // For phone-OTP users the auth user may have a synthetic email
  // (972...@b2b-sms.axelio.app) and a phone without the + prefix, so the
  // original E.164 phone is needed for reliable lookup.
  const classified = await classifyAuthenticatedUser({
    id:    user.id,
    email: user.email,
    phone: originalPhone || user.phone,
  });

  switch (classified.type) {

    case 'b2b_client': {
      const session = await buildClientSession(
        classified.b2bSession!,
        classified.b2bSession!.companies,
      );
      if (!session) {
        await supabase.auth.signOut();
        return 'פרטי הלקוח לא נמצאו — צור קשר עם הספק שלך';
      }
      seedB2BUser(session);
      window.location.replace('/b2b/catalog');
      return null;
    }

    case 'field_agent': {
      // Phase 74: buildAgentSession is async (RPC for available companies).
      const agentSession = await buildAgentSession(classified.agentSession!);
      seedB2BUser(agentSession);
      window.location.replace('/b2b/agent');
      return null;
    }

    case 'dual_role':
      // Signal to the caller to show the Role Selector screen
      return {
        dual_role:    true,
        b2bSession:   classified.b2bSession!,
        agentSession: classified.agentSession!,
      };

    default:
      await supabase.auth.signOut();
      return 'הפרטים שלך לא נמצאו במערכת — צור קשר עם הספק שלך לקבלת גישה';
  }
}

// ─── 6-digit OTP input component ─────────────────────────────────────────────

interface OtpInputProps {
  value:    string[];        // exactly 6 slots
  onChange: (v: string[]) => void;
  disabled: boolean;
}

function OtpInput({ value, onChange, disabled }: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const focus = (i: number) => refs.current[i]?.focus();

  const handleChange = (idx: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    const next  = [...value];
    next[idx]   = digit;
    onChange(next);
    if (digit && idx < 5) focus(idx + 1);
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) focus(idx - 1);
    if (e.key === 'ArrowLeft'  && idx < 5) focus(idx + 1);
    if (e.key === 'ArrowRight' && idx > 0) focus(idx - 1);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!digits) return;
    const next = [...value];
    for (let i = 0; i < 6; i++) next[i] = digits[i] ?? '';
    onChange(next);
    focus(Math.min(digits.length, 5));
  };

  return (
    <div className="flex justify-center gap-2" dir="ltr">
      {value.map((digit, idx) => (
        <input
          key={idx}
          ref={el => { refs.current[idx] = el; }}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={e => handleChange(idx, e.target.value)}
          onKeyDown={e => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          onFocus={e => e.target.select()}
          aria-label={`ספרה ${idx + 1} מתוך 6`}
          className={[
            'w-11 h-14 text-center text-xl font-bold rounded-xl border-2',
            'transition-all duration-150 outline-none',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            digit
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-gray-200 bg-white text-gray-900',
            'focus:border-primary-500 focus:ring-2 focus:ring-primary-200',
          ].join(' ')}
        />
      ))}
    </div>
  );
}

// ─── Tab types ────────────────────────────────────────────────────────────────

type LoginTab  = 'otp' | 'password';
type OtpStep   = 'contact' | 'pin';
type PwMode    = 'login' | 'forgot' | 'forgot_sent';

// ─── Main Page ────────────────────────────────────────────────────────────────

export function B2BLoginPage() {
  const navigate        = useNavigate();
  const location        = useLocation();
  const { seedB2BUser } = useB2BUser();

  // ── Dual-role selector state ────────────────────────────────────────────────
  // Set when classifyAndRoute returns { dual_role: true, … } so we can display
  // the RoleSelector overlay instead of auto-routing.
  const [dualRole, setDualRole] = useState<{
    b2bSession:   B2bClientSession;
    agentSession: AgentSession;
  } | null>(null);

  // ── Active tab ─────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<LoginTab>('otp');

  // ── Shared error for access-denied (both tabs) ─────────────────────────────
  // Also reads from location.state.accessError so that B2BLayout's
  // 'unknown' handler can sign the user out and redirect here with a
  // pre-populated error message (Phase 38.4 — infinite loading fix).
  const [accessError, setAccessError] = useState<string | null>(
    (location.state as { accessError?: string } | null)?.accessError ?? null,
  );

  // Clear the location state so the error doesn't reappear on refresh.
  useEffect(() => {
    if ((location.state as { accessError?: string } | null)?.accessError) {
      // Replace history entry with clean state so a hard-refresh doesn't
      // re-show the same error without cause.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // TAB A — OTP state
  // ─────────────────────────────────────────────────────────────────────────
  const [otpStep,        setOtpStep]        = useState<OtpStep>('contact');
  const [contact,        setContact]        = useState('');
  const [sendLoading,    setSendLoading]    = useState(false);
  const [sendError,      setSendError]      = useState<string | null>(null);
  const [otpDigits,      setOtpDigits]      = useState<string[]>(Array(6).fill(''));
  const [verifyLoading,  setVerifyLoading]  = useState(false);
  const [verifyError,    setVerifyError]    = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpMethod, setOtpMethod] = useState<'sms' | 'whatsapp' | null>(null);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const contactType: 'email' | 'phone' | 'unknown' = isEmail(contact)
    ? 'email' : isPhone(contact) ? 'phone' : 'unknown';

  const displayContact = contactType === 'phone'
    ? normalisePhone(contact) : contact.trim();

  const startCooldown = (secs = 30) => {
    setResendCooldown(secs);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const doSendOtp = async (addr: { email?: string; phone?: string }, method: 'sms' | 'whatsapp' = 'whatsapp') => {
    if (addr.email) {
      const { error } = await supabase.auth.signInWithOtp({
        email: addr.email, options: { shouldCreateUser: false },
      });
      return error;
    }
    if (method === 'sms') {
      if (!addr.phone) return new Error('מספר נייד חסר');
      // SMS goes through the same Edge Function as WhatsApp, so it works
      // even without a pre-existing Supabase Auth user.
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-otp`;
      const resp = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ action: 'send', phone: addr.phone, method: 'sms' }),
      });
      if (!resp.ok) {
        const respBody = await resp.json().catch(() => ({}));
        return { message: respBody.error ?? 'שליחת קוד ב-SMS נכשלה' } as Error;
      }
      return null;
    }
    // Phone → WhatsApp OTP via Edge Function
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-otp`;
    const resp = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ action: 'send', phone: addr.phone }),
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      return { message: body.error ?? 'שליחת קוד ב-WhatsApp נכשלה' } as Error;
    }
    return null;
  };

  const handleSendOtp = async (method?: 'sms' | 'whatsapp') => {
    setAccessError(null);
    setSendError(null);
    const val = contact.trim();
    if (!val)                   { setSendError('נא להזין אימייל או מספר נייד'); return; }
    if (contactType === 'unknown') {
      setSendError('פרמט לא תקין — הזן אימייל (user@domain.com) או נייד (05X-XXXXXXX)');
      return;
    }
    const selectedMethod = contactType === 'phone' ? (method ?? 'whatsapp') : 'whatsapp';
    setSendLoading(true);
    try {
      const err = await doSendOtp(
        contactType === 'email'
          ? { email: val }
          : { phone: normalisePhone(val) },
        selectedMethod,
      );
      if (err) {
        setSendError(
          err.message.toLowerCase().includes('signups not allowed')
            ? 'כתובת זו אינה רשומה במערכת — צור קשר עם הספק שלך'
            : err.message,
        );
        return;
      }
      startCooldown(30);
      setOtpMethod(selectedMethod);
      setOtpDigits(Array(6).fill(''));
      setVerifyError(null);
      setOtpStep('pin');
    } finally {
      setSendLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const token = otpDigits.join('');
    setVerifyError(null);
    setAccessError(null);
    if (token.length < 6) { setVerifyError('נא להזין את כל 6 הספרות'); return; }
    setVerifyLoading(true);
    try {
      if (contactType === 'email') {
        // Email OTP — standard Supabase auth flow
        const otpResult = await supabase.auth.verifyOtp({ email: displayContact, token, type: 'email' });
        if (otpResult.error) { setVerifyError('הקוד שגוי או שפג תוקפו — נסה שנית'); return; }
        const user = otpResult.data.user;
        if (!user) { setVerifyError('שגיאה בלתי צפויה — נסה שנית'); return; }
        const routeResult = await classifyAndRoute(user, seedB2BUser);
        if (routeResult === null) {
          // success — navigation already triggered inside classifyAndRoute
        } else if (typeof routeResult === 'string') {
          setAccessError(routeResult);
          setOtpStep('contact');
        } else if ('dual_role' in routeResult) {
          setDualRole({ b2bSession: routeResult.b2bSession, agentSession: routeResult.agentSession });
        }
      } else if (otpMethod === 'sms') {
        // SMS OTP — verify through the same Edge Function as WhatsApp
        // (works without a pre-existing Supabase Auth user)
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-otp`;
        const resp = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ action: 'verify', phone: displayContact, code: token, method: 'sms' }),
        });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok || !body.success) {
          setVerifyError(body.error ?? 'הקוד שגוי או שפג תוקפו — נסה שנית');
          return;
        }

        // Establish Supabase Auth session if possible (needed for RLS queries later)
        if (body.token_hash) {
          const otpResult = await supabase.auth.verifyOtp({
            token_hash: body.token_hash,
            type: 'magiclink',
          });
          if (otpResult.error) {
            setVerifyError('אימות הסשן נכשל — נסה שנית');
            return;
          }
        }

        // Route using the edge function's already-fetched records (avoids
        // a second RPC call that can race with the just-created auth session).
        const smsRouteResult = await routeFromEdgeData(
          body.clientRecords ?? [], body.agentRecords ?? [],
          seedB2BUser,
        );
        applyRouteResult(smsRouteResult, setAccessError, setOtpStep, setDualRole);
      } else {
        // Phone → WhatsApp OTP verification via Edge Function
        // Returns clientRecords + agentRecords directly (no Supabase Auth user required)
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-otp`;
        const resp = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ action: 'verify', phone: displayContact, code: token }),
        });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok || !body.success) {
          setVerifyError(body.error ?? 'הקוד שגוי או שפג תוקפו — נסה שנית');
          return;
        }

        // Establish Supabase Auth session if possible (needed for RLS queries later)
        if (body.token_hash) {
          const otpResult = await supabase.auth.verifyOtp({
            token_hash: body.token_hash,
            type: 'magiclink',
          });
          if (otpResult.error) {
            setVerifyError('אימות הסשן נכשל — נסה שנית');
            return;
          }
        }

        // Route using the edge function's already-fetched records (avoids
        // a second RPC call that can race with the just-created auth session).
        const waRouteResult = await routeFromEdgeData(
          body.clientRecords ?? [], body.agentRecords ?? [],
          seedB2BUser,
        );
        applyRouteResult(waRouteResult, setAccessError, setOtpStep, setDualRole);
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setVerifyError(null);
    setOtpDigits(Array(6).fill(''));
    setSendLoading(true);
    try {
      const err = await doSendOtp(
        contactType === 'email'
          ? { email: displayContact }
          : { phone: displayContact },
        otpMethod ?? 'whatsapp',
      );
      if (err) setVerifyError(err.message);
      else     startCooldown(30);
    } finally {
      setSendLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // TAB B — Password state
  // ─────────────────────────────────────────────────────────────────────────
  const [pwEmail,       setPwEmail]       = useState('');
  const [pwPassword,    setPwPassword]    = useState('');
  const [pwShowPass,    setPwShowPass]    = useState(false);
  const [pwLoading,     setPwLoading]     = useState(false);
  const [pwError,       setPwError]       = useState<string | null>(null);

  // ── Forgot-password sub-mode ──────────────────────────────────────────────
  const [pwMode,        setPwMode]        = useState<PwMode>('login');
  const [forgotEmail,   setForgotEmail]   = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError,   setForgotError]   = useState<string | null>(null);

  const handleForgotPassword = async () => {
    setForgotError(null);
    const email = forgotEmail.trim();
    if (!email)          { setForgotError('נא להזין כתובת אימייל'); return; }
    if (!isEmail(email)) { setForgotError('כתובת האימייל אינה תקינה'); return; }
    setForgotLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      });
      if (error) {
        setForgotError(error.message);
        return;
      }
      setPwMode('forgot_sent');
    } finally {
      setForgotLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    setPwError(null);
    setAccessError(null);
    if (!pwEmail.trim())    { setPwError('נא להזין כתובת אימייל');  return; }
    if (!isEmail(pwEmail))  { setPwError('כתובת האימייל אינה תקינה'); return; }
    if (!pwPassword)        { setPwError('נא להזין סיסמה');          return; }

    setPwLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email:    pwEmail.trim(),
        password: pwPassword,
      });

      if (error) {
        // Surface friendly Hebrew messages for the most common errors
        if (
          error.message.toLowerCase().includes('invalid login') ||
          error.message.toLowerCase().includes('invalid credentials')
        ) {
          setPwError('אימייל או סיסמה שגויים — נסה שנית');
        } else if (error.message.toLowerCase().includes('email not confirmed')) {
          setPwError('האימייל לא אומת — בדוק את תיבת הדואר שלך');
        } else {
          setPwError(error.message);
        }
        return;
      }

      const user = data.user;
      if (!user) { setPwError('שגיאה בלתי צפויה — נסה שנית'); return; }

      const routeResult = await classifyAndRoute(user, seedB2BUser);
      if (routeResult === null) {
        // success — navigation already triggered
      } else if (typeof routeResult === 'string') {
        setAccessError(routeResult);
      } else if ('dual_role' in routeResult) {
        setDualRole({ b2bSession: routeResult.b2bSession, agentSession: routeResult.agentSession });
      }
    } finally {
      setPwLoading(false);
    }
  };

  // ── Switch tab — clear errors ────────────────────────────────────────────
  const switchTab = (tab: LoginTab) => {
    setActiveTab(tab);
    setAccessError(null);
    setSendError(null);
    setVerifyError(null);
    setPwError(null);
    setOtpStep('contact');
    setOtpMethod(null);
    setDualRole(null);
    setPwMode('login');
    setForgotEmail('');
    setForgotError(null);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div
      dir="rtl"
      className="min-h-screen bg-gradient-to-b from-primary-600 via-primary-500 to-primary-400
                 flex flex-col items-center justify-center px-4 py-10"
    >
      {/* Brand header */}
      <div className="mb-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 mb-4
                        bg-white/20 backdrop-blur rounded-2xl shadow-lg">
          <ShoppingBag className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white tracking-tight">מערכת הזמנות B2B</h1>
        <p className="mt-1 text-primary-100 text-sm">כניסה מאובטחת ללקוחות רשומים</p>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">

        {/* ── Login method toggle tabs ───────────────────────────────────── */}
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => switchTab('otp')}
            className={[
              'flex-1 flex items-center justify-center gap-2 py-4 text-sm font-semibold',
              'transition-colors focus:outline-none',
              activeTab === 'otp'
                ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50/50'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50',
            ].join(' ')}
          >
            <KeyRound className="w-4 h-4" />
            קוד אימות
          </button>
          <button
            onClick={() => switchTab('password')}
            className={[
              'flex-1 flex items-center justify-center gap-2 py-4 text-sm font-semibold',
              'transition-colors focus:outline-none',
              activeTab === 'password'
                ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50/50'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50',
            ].join(' ')}
          >
            <Lock className="w-4 h-4" />
            מייל וסיסמה
          </button>
        </div>

        <div className="px-6 pt-6 pb-8 space-y-5">

          {/* ── Dual-role selector (replaces login form when triggered) ────── */}
          {dualRole ? (
            <>
              <RoleSelector
                b2bSession={dualRole.b2bSession}
                agentSession={dualRole.agentSession}
                onSelectClient={() => {
                  buildClientSession(dualRole.b2bSession, dualRole.b2bSession.companies).then(session => {
                    if (session) {
                      seedB2BUser(session);
                      window.location.replace('/b2b/catalog');
                    }
                  });
                }}
                onSelectAgent={() => {
                  // Phase 74: buildAgentSession is async (RPC for companies).
                  buildAgentSession(dualRole.agentSession)
                    .then(s => {
                      seedB2BUser(s);
                      window.location.replace('/b2b/agent');
                    })
                    .catch(err => {
                      console.error('[Phase 74] buildAgentSession failed:', err);
                    });
                }}
              />
              <button
                onClick={() => setDualRole(null)}
                className="w-full text-center text-xs text-gray-400 hover:text-gray-600 pt-1"
              >
                ← חזור להתחברות
              </button>
            </>
          ) : (
          <>

          {/* ── Shared access-denied banner ─────────────────────────────── */}
          {accessError && (
            <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border
                            border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{accessError}</span>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB A — OTP
          ══════════════════════════════════════════════════════════════ */}
          {activeTab === 'otp' && (

            <>
              {/* Step indicator mini-bar */}
              <div className="flex gap-1.5">
                <div className="flex-1 h-1 rounded-full bg-primary-500" />
                <div className={`flex-1 h-1 rounded-full transition-colors duration-500
                                 ${otpStep === 'pin' ? 'bg-primary-500' : 'bg-gray-100'}`} />
              </div>

              {/* ── Step 1: contact input ────────────────────────────── */}
              {otpStep === 'contact' && (
                <>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">שלח קוד אימות</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      הזן אימייל או מספר נייד — נשלח לך קוד חד-פעמי ב-SMS או WhatsApp
                    </p>
                  </div>

                  {sendError && (
                    <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border
                                    border-red-200 rounded-xl text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{sendError}</span>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <label className="block text-sm font-semibold text-gray-700">
                      אימייל או מספר נייד
                    </label>
                    <div className="relative">
                      {contactType === 'email'
                        ? <Mail  className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500 pointer-events-none" />
                        : <Phone className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      }
                      <input
                        type="text"
                        inputMode="email"
                        autoComplete="username"
                        autoFocus
                        value={contact}
                        onChange={e => { setContact(e.target.value); setSendError(null); setAccessError(null); }}
                        onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                        placeholder="user@example.com  /  0500000000"
                        dir="ltr"
                        disabled={sendLoading}
                        className="w-full pr-10 pl-4 py-3.5 rounded-xl border border-gray-200
                                   text-sm bg-white placeholder:text-gray-400 text-gray-900
                                   focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent
                                   disabled:opacity-60 transition"
                      />
                    </div>
                    <p className="text-xs text-gray-400">
                      דוגמה: 050-1234567 · store@example.com
                    </p>
                  </div>

                  {contactType === 'phone' ? (
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handleSendOtp('sms')}
                        disabled={sendLoading || !contact.trim()}
                        className="flex items-center justify-center gap-2 py-3.5 rounded-xl
                                   bg-primary-600 hover:bg-primary-700 active:bg-primary-800
                                   text-white font-semibold text-sm
                                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {sendLoading
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Phone className="w-4 h-4" />
                        }
                        {sendLoading ? 'שולח…' : 'שלח ב-SMS'}
                      </button>
                      <button
                        onClick={() => handleSendOtp('whatsapp')}
                        disabled={sendLoading || !contact.trim()}
                        className="flex items-center justify-center gap-2 py-3.5 rounded-xl
                                   bg-green-600 hover:bg-green-700 active:bg-green-800
                                   text-white font-semibold text-sm
                                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {sendLoading
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Send className="w-4 h-4" />
                        }
                        {sendLoading ? 'שולח…' : 'שלח ב-WhatsApp'}
                      </button>
                    </div>
                  ) : (
                  <button
                    onClick={() => handleSendOtp()}
                    disabled={sendLoading || !contact.trim()}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                               bg-primary-600 hover:bg-primary-700 active:bg-primary-800
                               text-white font-semibold text-sm
                               disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendLoading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <ArrowRight className="w-4 h-4" />
                    }
                    {sendLoading ? 'שולח קוד…' : 'שלח קוד אימות'}
                  </button>
                  )}
                </>
              )}

              {/* ── Step 2: OTP pin entry ────────────────────────────── */}
              {otpStep === 'pin' && (
                <>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">הזן קוד אימות</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      שלחנו קוד של 6 ספרות אל{' '}
                      <span className="font-medium text-gray-700 break-all" dir="ltr">
                        {displayContact}
                      </span>
                    </p>
                  </div>

                  <div className="flex items-center gap-2 px-3.5 py-2.5 bg-green-50
                                  border border-green-200 rounded-xl text-sm text-green-700">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span>הקוד נשלח ב-{otpMethod === 'sms' ? 'SMS' : 'WhatsApp'} — תוקפו 10 דקות</span>
                  </div>

                  {verifyError && (
                    <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border
                                    border-red-200 rounded-xl text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{verifyError}</span>
                    </div>
                  )}

                  <OtpInput
                    value={otpDigits}
                    onChange={setOtpDigits}
                    disabled={verifyLoading}
                  />

                  <button
                    onClick={handleVerifyOtp}
                    disabled={verifyLoading || otpDigits.join('').length < 6}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                               bg-primary-600 hover:bg-primary-700 active:bg-primary-800
                               text-white font-semibold text-sm
                               disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {verifyLoading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <CheckCircle2 className="w-4 h-4" />
                    }
                    {verifyLoading ? 'מאמת…' : 'אמת והיכנס'}
                  </button>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-100" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="px-3 bg-white text-xs text-gray-400">לא קיבלת?</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <button
                      onClick={() => { setOtpStep('contact'); setSendError(null); setVerifyError(null); }}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      ← שנה פרטים
                    </button>
                    <button
                      onClick={handleResend}
                      disabled={resendCooldown > 0 || sendLoading}
                      className="flex items-center gap-1.5 text-primary-600 hover:text-primary-700
                                 disabled:text-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${sendLoading ? 'animate-spin' : ''}`} />
                      {resendCooldown > 0 ? `שלח שוב (${resendCooldown})` : 'שלח קוד חדש'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB B — Email + Password
          ══════════════════════════════════════════════════════════════ */}
          {activeTab === 'password' && (
            <>
              {/* ── Login sub-mode (heading + fields + button) ──────────────── */}
              {pwMode === 'login' && (
                <>
              <div>
                <h2 className="text-lg font-bold text-gray-900">כניסה עם סיסמה</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  הזן את כתובת האימייל והסיסמה שלך
                </p>
              </div>

              {pwError && (
                <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border
                                border-red-200 rounded-xl text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{pwError}</span>
                </div>
              )}

              {/* Email field */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">אימייל</label>
                <div className="relative">
                  <Mail className="absolute right-3.5 top-1/2 -translate-y-1/2
                                   w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoFocus
                    value={pwEmail}
                    onChange={e => { setPwEmail(e.target.value); setPwError(null); setAccessError(null); }}
                    onKeyDown={e => e.key === 'Enter' && handlePasswordLogin()}
                    placeholder="user@example.com"
                    dir="ltr"
                    disabled={pwLoading}
                    className="w-full pr-10 pl-4 py-3.5 rounded-xl border border-gray-200
                               text-sm bg-white placeholder:text-gray-400 text-gray-900
                               focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent
                               disabled:opacity-60 transition"
                  />
                </div>
              </div>

              {/* Password field */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">סיסמה</label>
                <div className="relative">
                  <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2
                                   w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type={pwShowPass ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={pwPassword}
                    onChange={e => { setPwPassword(e.target.value); setPwError(null); setAccessError(null); }}
                    onKeyDown={e => e.key === 'Enter' && handlePasswordLogin()}
                    placeholder="••••••••"
                    dir="ltr"
                    disabled={pwLoading}
                    className="w-full pr-10 pl-10 py-3.5 rounded-xl border border-gray-200
                               text-sm bg-white placeholder:text-gray-400 text-gray-900
                               focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent
                               disabled:opacity-60 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setPwShowPass(p => !p)}
                    tabIndex={-1}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2
                               text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={pwShowPass ? 'הסתר סיסמה' : 'הצג סיסמה'}
                  >
                    {pwShowPass
                      ? <EyeOff className="w-4 h-4" />
                      : <Eye    className="w-4 h-4" />
                    }
                  </button>
                </div>
              </div>
                </>
              )}

              {/* ── Login sub-mode (button + links) ──────────────────────────── */}
              {pwMode === 'login' && (
                <>
                  <button
                    onClick={handlePasswordLogin}
                    disabled={pwLoading || !pwEmail.trim() || !pwPassword}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                               bg-primary-600 hover:bg-primary-700 active:bg-primary-800
                               text-white font-semibold text-sm
                               disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {pwLoading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <ArrowRight className="w-4 h-4" />
                    }
                    {pwLoading ? 'מתחבר…' : 'התחבר'}
                  </button>

                  {/* Forgot password + switch-to-OTP row */}
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <button
                      onClick={() => switchTab('otp')}
                      className="text-primary-600 font-medium hover:underline"
                    >
                      כניסה בקוד אימות
                    </button>
                    <button
                      onClick={() => {
                        setForgotEmail(pwEmail);  // pre-fill with whatever was typed
                        setForgotError(null);
                        setPwMode('forgot');
                      }}
                      className="text-gray-500 hover:text-gray-700 hover:underline"
                    >
                      שכחתי סיסמה
                    </button>
                  </div>
                </>
              )}

              {/* ── Forgot-password form ──────────────────────────────────────── */}
              {pwMode === 'forgot' && (
                <>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">איפוס סיסמה</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      הזן את כתובת האימייל שלך — נשלח לך קישור לאיפוס הסיסמה
                    </p>
                  </div>

                  {forgotError && (
                    <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border
                                    border-red-200 rounded-xl text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{forgotError}</span>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <label className="block text-sm font-semibold text-gray-700">אימייל</label>
                    <div className="relative">
                      <Mail className="absolute right-3.5 top-1/2 -translate-y-1/2
                                       w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        autoFocus
                        value={forgotEmail}
                        onChange={e => { setForgotEmail(e.target.value); setForgotError(null); }}
                        onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
                        placeholder="user@example.com"
                        dir="ltr"
                        disabled={forgotLoading}
                        className="w-full pr-10 pl-4 py-3.5 rounded-xl border border-gray-200
                                   text-sm bg-white placeholder:text-gray-400 text-gray-900
                                   focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent
                                   disabled:opacity-60 transition"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleForgotPassword}
                    disabled={forgotLoading || !forgotEmail.trim()}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                               bg-primary-600 hover:bg-primary-700 active:bg-primary-800
                               text-white font-semibold text-sm
                               disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {forgotLoading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Send className="w-4 h-4" />
                    }
                    {forgotLoading ? 'שולח…' : 'שלח קישור לאיפוס'}
                  </button>

                  <button
                    onClick={() => setPwMode('login')}
                    className="w-full text-center text-xs text-gray-400 hover:text-gray-600"
                  >
                    ← חזור להתחברות
                  </button>
                </>
              )}

              {/* ── Sent confirmation ──────────────────────────────────────────── */}
              {pwMode === 'forgot_sent' && (
                <>
                  <div className="flex flex-col items-center gap-3 py-4 text-center">
                    <div className="flex items-center justify-center w-14 h-14 rounded-full
                                    bg-green-100">
                      <CheckCircle2 className="w-7 h-7 text-green-600" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">הקישור נשלח!</h2>
                    <p className="text-sm text-gray-500">
                      שלחנו קישור לאיפוס סיסמה אל{' '}
                      <span className="font-medium text-gray-700 break-all" dir="ltr">
                        {forgotEmail}
                      </span>
                    </p>
                    <p className="text-xs text-gray-400">
                      בדוק את תיבת הדואר (כולל ספאם) · הקישור תקף ל-60 דקות
                    </p>
                  </div>

                  <button
                    onClick={() => { setPwMode('login'); setForgotEmail(''); }}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                               border-2 border-gray-200 hover:border-gray-300
                               text-gray-700 font-semibold text-sm transition-colors"
                  >
                    ← חזור להתחברות
                  </button>
                </>
              )}
            </>
          )}

          </> /* end non-dual-role block */
          )} {/* end dualRole ternary */}

        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 flex flex-col items-center gap-2">
        <a
          href="/login"
          className="text-sm text-primary-100 hover:text-white underline underline-offset-2 transition-colors"
        >
          כניסה למערכת הראשית של Axelio
        </a>
        <p className="text-xs text-primary-200 text-center">
          מערכת מאובטחת · פועלת על גבי Supabase Auth
        </p>
      </div>
    </div>
  );
}
